/**
 * Main navigation bar component — Liquid Blob style.
 *
 * A morphing gradient blob smoothly slides to whichever nav link
 * is active. Background uses an animated gradient mesh.
 */
import { useRef, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faUser } from '@fortawesome/pro-solid-svg-icons';
import { useNavBarState, PROFILE_LINKS, GROUPS_NAV } from './navbars/useNavBarState';
import BrewAiIcon from '@/components/brewai/BrewAiIcon';
import BrewDbIcon from '@/components/brewdb/BrewDbIcon';
import { ATTN, useAttention } from '@/attention';
import { AttentionDot } from '@/attention/AttentionDot';
import { attentionAriaLabel } from '@/attention/attentionText';

const styles = `
  @keyframes blob-bg-shift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  @keyframes blob-breathe {
    0%, 100% { transform: scaleY(1); }
    50% { transform: scaleY(1.08); }
  }
  .blob-bar {
    background: linear-gradient(270deg, rgba(5, 10, 25, 0.9), rgba(12, 18, 40, 0.9), rgba(8, 12, 30, 0.9));
    background-size: 400% 100%;
    animation: blob-bg-shift 15s ease-in-out infinite;
  }
  .blob-indicator {
    position: absolute;
    top: 8px;
    bottom: 8px;
    border-radius: 12px;
    pointer-events: none;
    background: linear-gradient(135deg, rgb(var(--brew-blue-light) / 0.18), rgba(129, 140, 248, 0.12), rgb(var(--brew-blue-light) / 0.15));
    border: 1px solid rgb(var(--brew-blue-light) / 0.15);
    box-shadow: 0 0 20px rgb(var(--brew-blue-light) / 0.08), inset 0 0 12px rgb(var(--brew-blue-light) / 0.04);
    transition: left 0.5s cubic-bezier(0.4, 0, 0.2, 1),
                width 0.5s cubic-bezier(0.4, 0, 0.2, 1),
                opacity 0.3s ease;
    animation: blob-breathe 4s ease-in-out infinite;
  }
  .blob-indicator::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgb(var(--brew-blue-light) / 0.1), transparent, rgba(129, 140, 248, 0.08));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .blob-indicator:hover::before {
    opacity: 1;
  }
  .blob-link {
    position: relative;
    z-index: 1;
    padding: 6px 14px;
    font-size: 13px;
    font-weight: 600;
    transition: color 0.3s ease;
    white-space: nowrap;
  }
  .blob-link-active {
    color: #d4eaf5;
    font-weight: 700;
  }
  .blob-link-inactive {
    color: rgb(var(--aurora-text-muted) / 0.85);
  }
  .blob-link-inactive:hover {
    color: #d4eaf5;
  }
`;

/**
 * A top-level nav link that hosts the admin attention dot. The link is
 * `relative` because the dot is absolutely positioned; it sits inside the
 * link's box, so `measureBlob`'s width math is unchanged. Below md (the open
 * mobile menu) a `block` link would span the menu and park the dot far from
 * the label, so it shrink-wraps there (`w-fit`); from md up it is the same
 * `block`, auto-width box as its siblings.
 */
function AdminNavLink({ to, active, label }: Readonly<{ to: string; active: boolean; label: string }>) {
  const summary = useAttention(ATTN.admin);
  return (
    <Link
      to={to}
      data-active={active}
      aria-label={summary.total > 0 ? attentionAriaLabel(label, summary) : undefined}
      className={`blob-link relative block w-fit md:w-auto ${active ? 'blob-link-active' : 'blob-link-inactive'}`}
    >
      {label}
      <AttentionDot summary={summary} />
    </Link>
  );
}

export default function NavigationBar() {
  const adminAttention = useAttention(ATTN.admin);
  // The dropdown's ref is taken out on its own, so `nav` holds no ref and
  // reading its fields during render is plain data (react-hooks/refs).
  const { dropdownRef, ...nav } = useNavBarState();
  // Groups moves into the profile menu only when the bar is narrow AND that
  // menu exists (it needs the loaded profile).
  const groupsInMenu = !nav.groupsInBar && !!nav.userProfile;
  const navListRef = useRef<HTMLUListElement>(null);
  const [blob, setBlob] = useState({ left: 0, width: 0, opacity: 0 });
  // Pre-generated target for the "Build" nav so it can be a real <a href>
  // (enables ctrl/⌘/middle-click + right-click "open in new tab"). Each
  // click advances it so repeated new-tab opens create distinct models.
  const [newModelId, setNewModelId] = useState(() => crypto.randomUUID());

  const measureBlob = useCallback(() => {
    const list = navListRef.current;
    if (!list) return;
    const activeEl = list.querySelector('[data-active="true"]');
    if (activeEl) {
      const listRect = list.getBoundingClientRect();
      const linkRect = activeEl.getBoundingClientRect();
      setBlob({
        left: linkRect.left - listRect.left,
        width: linkRect.width,
        opacity: 1,
      });
    } else {
      setBlob(prev => ({ ...prev, opacity: 0 }));
    }
  }, []);

  useEffect(() => {
    measureBlob();
  }, [nav.path, nav.visibleNavItems, measureBlob]);

  useEffect(() => {
    window.addEventListener('resize', measureBlob);
    return () => window.removeEventListener('resize', measureBlob);
  }, [measureBlob]);

  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) return;
    // fire-and-forget: document.fonts.ready does not reject and measureBlob only reads layout
    void document.fonts.ready.then(measureBlob);
  }, [measureBlob]);

  useEffect(() => {
    const list = navListRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measureBlob);
    ro.observe(list);
    list.querySelectorAll('[data-active]').forEach(el => ro.observe(el));
    return () => ro.disconnect();
  }, [measureBlob, nav.visibleNavItems]);

  return (
    <>
      <style>{styles}</style>
      <nav
        className={`blob-bar fixed top-0 inset-x-0 z-50 flex items-center h-[50px] px-4 select-none ${nav.capcoEnabled ? 'mt-[21px]' : ''}`}
        style={{
          backdropFilter: 'blur(20px) saturate(1.2)',
          borderBottom: '1px solid rgb(var(--brew-blue-light) / 0.06)',
        }}
        role="navigation"
      >
        <div className="flex items-center w-full">
          <Link to="/dashboard" className="flex items-center mr-6 shrink-0 group">
            {nav.isActive('brewdb') ? (
              /* Contextual masthead: inside the brewDB UI the app bar carries the
                 brewDB lockup (spec ratios: mark 1.6× wordmark size, gap 0.45×). */
              <span className="flex items-center gap-[9px] transition-transform duration-300 group-hover:scale-105">
                <BrewDbIcon cut="full" paint="primary" className="text-[32px]" />
                <span className="text-[20px] font-bold tracking-tight leading-none text-aurora-text">
                  brew<span className="text-brew-blue-light">DB</span>
                </span>
              </span>
            ) : (
              <img src="/assets/icons/brewlytics-icon.svg" alt="Brewlytics" className="h-8 transition-transform duration-300 group-hover:scale-105" />
            )}
          </Link>

          <button
            className="relative md:hidden ml-auto size-9 rounded-lg inline-flex items-center justify-center text-aurora-text-muted/85"
            onClick={() => nav.setMobileMenuOpen(!nav.mobileMenuOpen)}
            aria-label={attentionAriaLabel('Toggle navigation', adminAttention)}
          >
            <FontAwesomeIcon icon={faBars} />
            <AttentionDot summary={adminAttention} />
          </button>

          <div
            className={`${nav.mobileMenuOpen ? 'flex' : 'hidden'} md:flex flex-col md:flex-row items-start md:items-center w-full absolute md:relative top-full left-0 md:top-auto p-4 md:p-0 z-50`}
            style={nav.mobileMenuOpen ? { background: 'rgba(8, 12, 30, 0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgb(var(--brew-blue-light) / 0.06)' } : undefined}
          >
            <ul ref={navListRef} className="relative flex flex-col md:flex-row gap-0.5 list-none">
              <div
                className="blob-indicator hidden md:block"
                style={{
                  left: blob.left,
                  width: blob.width,
                  opacity: blob.opacity,
                }}
              />

              {nav.authenticated && nav.visibleNavItems.map(item => {
                const active = nav.isActive(item.segment);
                const link = item.segment === 'admin' ? (
                  <AdminNavLink to={item.to} active={active} label={item.label} />
                ) : (
                  <Link
                    to={item.to}
                    data-active={active}
                    className={`blob-link block ${active ? 'blob-link-active' : 'blob-link-inactive'}`}
                  >
                    {item.label}
                  </Link>
                );
                return (
                  <li key={item.segment}>
                    {item.segment === 'build' ? (
                      <Link
                        to={`/build/${newModelId}`}
                        data-active={active}
                        aria-current={active ? 'page' : undefined}
                        className={`blob-link block ${active ? 'blob-link-active' : 'blob-link-inactive'}`}
                        style={active ? { cursor: 'default' } : undefined}
                        onClick={e => {
                          // Already on the build canvas: a plain left-click must
                          // not mint a new model over (possibly unsaved) work.
                          // Modifier / middle clicks still open a fresh model in
                          // a new tab via the native <a href>.
                          if (
                            active &&
                            e.button === 0 &&
                            !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey
                          ) {
                            e.preventDefault();
                            return;
                          }
                          // Advance to a fresh id so the next new model is distinct.
                          setNewModelId(crypto.randomUUID());
                        }}
                      >
                        {item.label}
                      </Link>
                    ) : link}
                  </li>
                );
              })}
            </ul>

            <div className="flex-1" />

            <ul
              data-nav-side="right"
              className="flex flex-col md:flex-row items-start md:items-center gap-0.5 list-none mt-2 md:mt-0"
            >
              {/* Groups (jorigin/brewauth#29 GB1): in the bar when it fits;
                  otherwise only in the mobile menu (below md) and the
                  profile menu (md and up), never unreachable. The profile
                  menu exists only once the profile has loaded, so until
                  then (or if that fetch failed) Groups stays in the bar. */}
              {nav.authenticated && (
                <li
                  data-nav-slot={groupsInMenu ? 'mobile' : 'bar'}
                  className={groupsInMenu ? 'md:hidden' : undefined}
                >
                  <Link
                    to={GROUPS_NAV.to}
                    aria-current={nav.isActive('groups') ? 'page' : undefined}
                    className={`blob-link flex items-center gap-2 ${nav.isActive('groups') ? 'blob-link-active' : 'blob-link-inactive'}`}
                    onClick={() => nav.setMobileMenuOpen(false)}
                  >
                    <FontAwesomeIcon icon={GROUPS_NAV.icon} aria-hidden="true" />
                    <span>{GROUPS_NAV.label}</span>
                  </Link>
                </li>
              )}
              {nav.authenticated && (
                <li>
                  <button onClick={nav.doLogout} className="blob-link blob-link-inactive block">
                    Logout
                  </button>
                </li>
              )}
              {nav.authenticated && nav.userProfile && (
                <li className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => nav.setUserDropdownOpen(!nav.userDropdownOpen)}
                    className={`blob-link flex items-center gap-2 ${nav.isActive('users') ? 'blob-link-active' : 'blob-link-inactive'}`}
                  >
                    <FontAwesomeIcon icon={faUser} />
                    <span>{nav.userLabel}</span>
                  </button>
                  {nav.userDropdownOpen && (
                    <ul className="absolute right-0 z-50 mt-2 min-w-44 py-2 list-none"
                      style={{
                        background: 'rgba(8, 12, 30, 0.94)',
                        backdropFilter: 'blur(24px)',
                        border: '1px solid rgb(var(--brew-blue-light) / 0.08)',
                        borderRadius: 14,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
                      }}
                    >
                      {groupsInMenu && (
                        <li data-nav-slot="profile-menu" className="hidden md:block">
                          <Link
                            to={GROUPS_NAV.to}
                            aria-current={nav.isActive('groups') ? 'page' : undefined}
                            className={`flex items-center gap-2.5 px-4 py-2 text-sm font-semibold transition-colors duration-200 hover:text-[#d4eaf5] ${
                              nav.isActive('groups')
                                ? 'bg-[rgb(var(--brew-blue-light)/0.12)] text-[#d4eaf5] shadow-[inset_2px_0_0_#3cb4e5]'
                                : 'text-aurora-text-muted/85'
                            }`}
                            onClick={() => nav.setUserDropdownOpen(false)}
                          >
                            <FontAwesomeIcon icon={GROUPS_NAV.icon} fixedWidth aria-hidden="true" /> {GROUPS_NAV.menuLabel}
                          </Link>
                        </li>
                      )}
                      {PROFILE_LINKS.map(link => (
                        <li key={link.context}>
                          <Link
                            to={nav.profileLink(link.context)}
                            className="flex items-center gap-2.5 px-4 py-2 text-sm font-semibold transition-colors duration-200 text-aurora-text-muted/85"
                            
                            onMouseEnter={e => e.currentTarget.style.color = '#d4eaf5'}
                            onMouseLeave={e => e.currentTarget.style.color = 'rgb(var(--aurora-text-muted) / 0.85)'}
                            onClick={() => nav.setUserDropdownOpen(false)}
                          >
                            {link.context === 'aiusage'
                              ? <span className="inline-flex w-[1.25em] justify-center"><BrewAiIcon /></span>
                              : <FontAwesomeIcon icon={link.icon} fixedWidth />} {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )}
            </ul>
          </div>
        </div>
      </nav>
      {nav.logoutConfirm}
    </>
  );
}
